import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, ChartOptions, TooltipItem } from 'chart.js';
import { de } from 'date-fns/locale';
import annotationPlugin from 'chartjs-plugin-annotation';
import { BaseChartComponent, Interval } from '../dashboard/components/energy-chart/base-chart.component';
import { PriceChartResponse } from '../../api/models/price-chart-response';
import { PriceChartPoint } from '../../api/models/price-chart-point';

/** Colors as in the grid chart: purchase black, sale grey. */
const CONSUMPTION_COLOR = '#333333';
const FEED_IN_COLOR = '#a0a0a0';
const MARKET_COLOR = '#7d9fc4';

/**
 * Line chart of a location's prices: consumption (Einkauf) and feed-in (Verkauf) price as two lines
 * without area, like the state of charge, plus the market price, hidden until picked in the legend.
 * A day shows quarter-hours as steps, a month the two-hour averages the backend condenses it to.
 */
@Component({
  selector: 'app-price-line-chart',
  templateUrl: '../dashboard/components/energy-chart/chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective],
})
export class PriceLineChartComponent extends BaseChartComponent implements OnChanges {
  @Input() override interval: Interval = 'day';
  @Input() date: Date = new Date();
  @Input() data: PriceChartResponse | undefined;

  override get unit(): string {
    return 'ct/kWh';
  }

  override set unit(value: string) {
    // the unit is always ct/kWh
  }

  constructor() {
    super();
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['interval'] || changes['date']) {
      this.initializeChartData();
    }
  }

  private initializeChartData() {
    const points = this.data?.points ?? [];
    const day = this.interval === 'day';
    const line = (label: string, color: string, value: (point: PriceChartPoint) => number | undefined) => ({
      label,
      data: points.map((point) => ({ x: new Date(point.timestamp ?? '').getTime(), y: value(point) ?? null })),
      borderColor: color,
      backgroundColor: color,
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 3,
      borderWidth: 2,
      tension: 0,
      // A quarter-hour price holds until the next one; the month's averages are joined like the SOC.
      stepped: day ? ('after' as const) : false,
      spanGaps: false,
    });

    const datasets: any[] = [
      line('Einkauf', CONSUMPTION_COLOR, (point) => point.consumptionPrice),
      line('Verkauf', FEED_IN_COLOR, (point) => point.feedInPrice),
      { ...line('Börsenpreis', MARKET_COLOR, (point) => point.marketPrice), borderDash: [4, 4], borderWidth: 1, hidden: true },
    ];

    this.chartType.set('line');
    let options = this.buildOptions(points);
    if (day) {
      options = this.addCurrentTimeLine(options, this.date);
    }
    this.chartOptions.set(options);
    this.chartData.set({ datasets });
    this.chart?.update();
  }

  private buildOptions(points: PriceChartPoint[]): ChartOptions<'line'> {
    const day = this.interval === 'day';
    const tooltip = this.baseTooltipOptions?.tooltip ?? {};
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: true, position: 'bottom' },
        tooltip: {
          ...tooltip,
          callbacks: {
            ...tooltip.callbacks,
            title: (items: TooltipItem<'line'>[]) => this.tooltipTitle(items),
            label: (item: TooltipItem<'line'>) =>
              item.parsed.y == null ? `${item.dataset.label}: –` : `${item.dataset.label}: ${this.format(item.parsed.y)} ct/kWh`,
            afterLabel: (item: TooltipItem<'line'>) => this.consumptionParts(item, points),
          },
        } as any,
      },
      scales: {
        y: {
          border: { display: false },
          title: { display: !this.isMobile(), text: 'ct/kWh' },
          ticks: { callback: (value) => `${value} ct` },
        },
        x: {
          type: 'time',
          min: this.data?.from ? new Date(this.data.from).getTime() : undefined,
          max: this.data?.to ? new Date(this.data.to).getTime() : undefined,
          time: day
            ? { unit: 'hour', tooltipFormat: 'HH:mm', displayFormats: { hour: 'HH:mm' } }
            : { unit: 'day', tooltipFormat: 'dd.MM. HH:mm', displayFormats: { day: this.isMobile() ? 'd' : 'dd.MM' } },
          adapters: { date: { locale: de } },
          ticks: {
            autoSkip: true,
            maxRotation: this.isMobile() || !day ? 90 : 0,
            minRotation: this.isMobile() || !day ? 90 : 0,
            maxTicksLimit: day ? (this.isMobile() ? 12 : 24) : undefined,
            font: { size: this.isMobile() ? 10 : 12 },
          },
          border: { display: false },
        },
      },
    };
  }

  /** The interval the hovered point stands for: a quarter-hour, or the two hours of a month point. */
  private tooltipTitle(items: TooltipItem<'line'>[]): string {
    if (!items.length) {
      return '';
    }
    const start = new Date(items[0].parsed.x);
    const end = new Date(start.getTime() + (this.data?.intervalMinutes ?? 15) * 60_000);
    const time = (date: Date) =>
      date.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', timeZone: this.timezone });
    const day = start.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', timeZone: this.timezone });
    return `${day} ${time(start)}–${time(end)}`;
  }

  /** Under the purchase price, what it is made of: Arbeitspreis plus Netzpreis. */
  private consumptionParts(item: TooltipItem<'line'>, points: PriceChartPoint[]): string | string[] {
    if (item.datasetIndex !== 0) {
      return '';
    }
    const point = points[item.dataIndex];
    if (point?.consumptionEnergyPrice == null || point.consumptionGridPrice == null) {
      return '';
    }
    return [
      `  Arbeitspreis: ${this.format(point.consumptionEnergyPrice)} ct/kWh`,
      `  Netzpreis: ${this.format(point.consumptionGridPrice)} ct/kWh`,
    ];
  }

  private format(value: number): string {
    return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
